import { PRODUCT_BRANDS } from 'types/products';
import { IAdditionalSettings } from '../components/PendingSettingsIndicator/PendingSettingsIndicator';
import { IData, IDestination, IproductReferenceDataInCountry } from '../types/orders';
import { LOCATION_PLACE_TYPE } from '../types/services';
import { CONFIG_STATUS_ID } from '../types/product.configuration.types';
import { checkAllActionsConfirmed } from './configurations';

const getBringTotalAdditionalSettings = (
  sku?: string,
  shipmentGroupId?: number,
  references?: IproductReferenceDataInCountry[],
  currentShipmentUnits?: number
): IAdditionalSettings => {
  const defaultValues = {
    total: 0,
    toBeCompleted: 0,
    completed: 0,
    notCompleted: 0,
    withUnconfirmedActions: 0,
    remainingMPNs: 0
  };

  if (!sku || !shipmentGroupId) return defaultValues;

  const reference = references?.find(reference => reference?.sku === sku);

  const items = reference?.items?.filter(item => item.shipmentGroupId === shipmentGroupId);

  if (!items || items.length === 0) return defaultValues;

  const totalUnitsInGroup = items.reduce((total, item) => total + 1, 0);

  const unitsToCalculate = currentShipmentUnits || totalUnitsInGroup;

  const proportionFactor = totalUnitsInGroup > 0 ? unitsToCalculate / totalUnitsInGroup : 1;

  const totalConfigurations = Math.round(
    items.reduce((total, item) => total + (item.configurations?.length || 0), 0) * proportionFactor
  );

  const toBeCompleted = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => config.status?.statusId === CONFIG_STATUS_ID.TO_COMPLETE)?.length || 0)
      );
    }, 0) * proportionFactor
  );

  const completed = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => {
          return (
            config.status?.statusId === CONFIG_STATUS_ID.COMPLETED &&
            checkAllActionsConfirmed(config?.additionalActions)
          );
        })?.length || 0)
      );
    }, 0) * proportionFactor
  );

  const notCompleted = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => config.status?.statusId === CONFIG_STATUS_ID.NOT_COMPLETED)?.length || 0)
      );
    }, 0) * proportionFactor
  );

  const withUnconfirmedActions = Math.round(
    items.reduce((total, item) => {
      return (
        total +
        (item.configurations?.filter(config => !checkAllActionsConfirmed(config?.additionalActions))?.length || 0)
      );
    }, 0) * proportionFactor
  );

  const remainingMPNs = Math.round(
    items.reduce((total, item) => {
      return total + (!item.mpnId && reference?.brand?.toLowerCase() === PRODUCT_BRANDS.APPLE.toLowerCase() ? 1 : 0);
    }, 0) * proportionFactor
  );

  return { total: totalConfigurations, toBeCompleted, completed, notCompleted, withUnconfirmedActions, remainingMPNs };
};

const searchLinkInData = (value: string) => {
  const regex = /(https?:\/\/[^\s]+)/g;
  const match = value.match(regex);
  if (match) return `<a target="_blank" href=${value}>${value.substring(0, 80)}...</a>`;
  return `${value}`;
};

export const formatDataToString = (data: IData[]): string => {
  return data
    .map(item => `<li><span class="titleList">${item.name}: </span>${searchLinkInData(item?.value)}</li>`)
    .join('');
};

const getShipmentDestinationText = (destination?: IDestination | null): string | null => {
  if (!destination) return null;
  const { place, receiverName, countryName } = destination;
  if (place === LOCATION_PLACE_TYPE.USER) {
    return receiverName || null;
  }
  if (place === LOCATION_PLACE_TYPE.OFFICE) {
    return countryName ? `Oficina (${countryName})` : null;
  }
  if (place === LOCATION_PLACE_TYPE.NUDOS) {
    return countryName ? `Bodega (${countryName})` : null;
  }
  return null;
};

export { getBringTotalAdditionalSettings, getShipmentDestinationText };
